import { useMemo, useState } from 'react';
import { CodeBlock, Tabs } from '../../components/ui';
import { Callout, DocSection, DocTable, Endpoint, LangCode, More, ParamTable, type Param } from '../../components/DocBlocks';
import { LANGS, chatSnippet, jsonSnippet, streamSnippet, toolsSnippet } from '../../snippets';
import { DocLink, PageHead, pretty, round2, useDocs } from './context';

/* Chat dan streaming */

const CHAT_MAIN: Param[] = [
  {
    name: 'model',
    type: 'string',
    required: true,
    desc: (
      <>
        ID model dari <code>GET /v1/models</code>, atau alias router seperti <code>auto:cheapest</code>. Lihat{' '}
        <DocLink slug="model" />.
      </>
    ),
  },
  {
    name: 'messages',
    type: 'array',
    required: true,
    desc: (
      <>
        Riwayat percakapan. Role yang diterima: <code>system</code>, <code>developer</code> (sama dengan <code>system</code>),{' '}
        <code>user</code>, <code>assistant</code>, dan <code>tool</code>. Isinya teks, atau bagian <code>text</code> dan{' '}
        <code>image_url</code> untuk model yang bisa membaca gambar.
      </>
    ),
  },
  {
    name: 'max_tokens / max_completion_tokens',
    type: 'integer',
    desc: (
      <>
        Batas panjang jawaban, sekaligus dasar saldo yang ditahan. Bila keduanya dikirim, <code>max_completion_tokens</code>{' '}
        yang dipakai. Bawaannya 4.096, dipotong ke batas model.
      </>
    ),
  },
  {
    name: 'stream',
    type: 'boolean',
    desc: (
      <>
        <code>true</code> untuk menerima jawaban bertahap. Lihat <a href="#streaming">Streaming</a>.
      </>
    ),
  },
  {
    name: 'temperature / top_p',
    type: 'number',
    desc: (
      <>
        Mengatur variasi jawaban. Dibuang untuk model yang tidak menerimanya, dan namanya dicantumkan di header{' '}
        <code>x-aiagg-ignored-params</code>. Lihat <a href="#parameter-umum">nilai yang umum</a>.
      </>
    ),
  },
];

const CHAT_MORE: Param[] = [
  {
    name: 'stream_options',
    type: 'object',
    desc: (
      <>
        <code>{'{"include_usage": true}'}</code> menambah satu chunk terakhir berisi <code>usage</code> dan <code>billing</code>.
      </>
    ),
  },
  {
    name: 'tools / tool_choice',
    type: 'array, string, atau object',
    desc: (
      <>
        Fungsi yang boleh dipanggil model. Lihat <DocLink slug="tools" />.
      </>
    ),
  },
  {
    name: 'response_format',
    type: 'object',
    desc: (
      <>
        <code>text</code>, <code>json_object</code>, atau <code>json_schema</code>. Lihat{' '}
        <DocLink slug="tools" hash="json">
          Output JSON
        </DocLink>
        .
      </>
    ),
  },
  {
    name: 'fallback_models',
    type: 'string[]',
    desc: (
      <>
        Sampai 5 model cadangan, dibatasi daftar cadangan proyek. Lihat{' '}
        <DocLink slug="model" hash="fallback">
          Model cadangan
        </DocLink>
        .
      </>
    ),
  },
  {
    name: 'reasoning_effort',
    type: 'string',
    desc: (
      <>
        <code>none</code> sampai <code>xhigh</code>, untuk model reasoning. Diteruskan bila provider mendukung.
      </>
    ),
  },
  { name: 'stop', type: 'string atau string[]', desc: 'Maksimal 4 urutan teks yang menghentikan jawaban.' },
  {
    name: 'presence_penalty / frequency_penalty / seed',
    type: 'number',
    desc: (
      <>
        Diteruskan bila provider punya padanannya. Bila tidak, dibuang dan dicantumkan di <code>x-aiagg-ignored-params</code>.
      </>
    ),
  },
  { name: 'parallel_tool_calls / user', type: 'boolean, string', desc: 'Diteruskan ke provider yang mendukung.' },
  { name: 'n', type: 'integer', desc: 'Hanya 1. Nilai lain dibalas 400.' },
];

type ResponseView = 'json' | 'sse';

export function ChatPage() {
  const { lang, setLang, model, text } = useDocs();
  const [view, setView] = useState<ResponseView>('json');

  const examples = useMemo(() => {
    const tp = text?.pricing;
    const amount = tp?.input_per_1m && tp.output_per_1m ? (18 * tp.input_per_1m + 42 * tp.output_per_1m) / 1e6 : 9.69;
    return {
      json: pretty({
        id: 'req_01M3264ME30XQNZNESGQ',
        object: 'chat.completion',
        model,
        provider: text?.provider ?? 'anthropic',
        choices: [
          {
            index: 0,
            message: { role: 'assistant', content: '1. Kopi Kabut Dago\n2. Seduh Braga\n3. Ruang Tunggu Kopi' },
            finish_reason: 'stop',
          },
        ],
        usage: {
          prompt_tokens: 18,
          completion_tokens: 42,
          total_tokens: 60,
          prompt_tokens_details: { cached_tokens: 0 },
          completion_tokens_details: { reasoning_tokens: 0 },
        },
        billing: { currency: 'IDR', amount: round2(amount), pricing_version_id: 'c21a81a9-…' },
      }),
      sse: [
        `data: {"id":"req_01M3…","object":"chat.completion.chunk","model":"${model}","choices":[{"index":0,"delta":{"role":"assistant","content":"Rintik"}}]}`,
        '',
        `data: {"id":"req_01M3…","object":"chat.completion.chunk","model":"${model}","choices":[{"index":0,"delta":{"content":" di atap seng"}}]}`,
        '',
        ': keep-alive',
        '',
        `data: {"id":"req_01M3…","object":"chat.completion.chunk","model":"${model}","choices":[],"usage":{"prompt_tokens":21,"completion_tokens":24,"total_tokens":45},"billing":{"currency":"IDR","amount":${round2(amount * 0.6)}}}`,
        '',
        'data: [DONE]',
      ].join('\n'),
    };
  }, [model, text]);

  return (
    <>
      <PageHead slug="chat">
        Kirim daftar pesan, terima jawaban dari model. Bentuk request dan responsnya sama dengan OpenAI Chat Completions.
      </PageHead>

      <DocSection id="request" title="Kirim request">
        <Endpoint method="POST" path="/v1/chat/completions" note="Sinkron atau streaming" />
        <LangCode
          code={chatSnippet(lang, { model, prompt: 'Buatkan 3 ide nama kedai kopi di Bandung.' })}
          lang={lang}
          langs={LANGS}
          onLang={setLang}
        />
      </DocSection>

      <DocSection id="parameter" title="Parameter">
        <ParamTable params={CHAT_MAIN} />
        <More label="Parameter lainnya" count={CHAT_MORE.length}>
          <ParamTable params={CHAT_MORE} />
          <p className="docs-caption">
            <code>logprobs</code> belum didukung dan dibalas 400. Parameter lain yang tidak dikenal diabaikan.
          </p>
        </More>
      </DocSection>

      <DocSection
        id="parameter-umum"
        title="Parameter yang sering dipakai"
        lead="Semuanya opsional. Tanpa parameter ini, model memakai nilai bawaannya."
      >
        <DocTable
          className="docs-common"
          head={['Parameter', 'Fungsinya', 'Nilai yang umum']}
          rows={[
            {
              key: 'temperature',
              cells: [
                <code>temperature</code>,
                'Seberapa bervariasi jawaban. Rendah: konsisten dan faktual. Tinggi: lebih beragam dan kreatif.',
                <>
                  <b>0–0,3</b> untuk ekstraksi data, klasifikasi, dan kode. <b>0,7–1</b> untuk brainstorming dan menulis.
                </>,
              ],
            },
            {
              key: 'top_p',
              cells: [
                <code>top_p</code>,
                'Cara lain mengatur variasi: model hanya memilih dari kata paling mungkin yang peluangnya berjumlah p. Ubah salah satu saja, temperature atau top_p.',
                <>
                  <b>1</b> (bawaan). <b>0,9</b> bila ingin sedikit lebih fokus.
                </>,
              ],
            },
            {
              key: 'max_tokens',
              cells: [
                <code>max_tokens</code>,
                'Batas panjang jawaban dalam token, kira-kira ¾ kata per token. Juga dasar saldo yang ditahan, jadi angka kecil berarti biaya maksimum kecil.',
                <>
                  <b>300–1.000</b> untuk chat. Lebih besar untuk dokumen panjang atau model reasoning, yang memakai token untuk berpikir
                  dulu.
                </>,
              ],
            },
            {
              key: 'stop',
              cells: [
                <code>stop</code>,
                'Teks yang menghentikan jawaban begitu muncul. Teks itu sendiri tidak ikut dikirim.',
                <>
                  Penanda seperti <code>&quot;###&quot;</code> atau <code>&quot;\n\n&quot;</code>. Maksimal 4.
                </>,
              ],
            },
            {
              key: 'reasoning_effort',
              cells: [
                <code>reasoning_effort</code>,
                'Seberapa lama model reasoning berpikir sebelum menjawab. Makin tinggi, makin teliti, tetapi lebih lambat dan memakai lebih banyak token.',
                <>
                  <b>low</b> untuk tugas ringan, <b>medium</b> atau <b>high</b> untuk analisis. <b>none</b> mematikan reasoning bila
                  model mendukung.
                </>,
              ],
            },
          ]}
        />
        <CodeBlock
          label="Contoh body"
          code={pretty({
            model,
            messages: [{ role: 'user', content: 'Ambil nama dan kota dari teks ini: …' }],
            temperature: 0.2,
            max_tokens: 300,
            stop: ['###'],
          })}
        />
        <Callout title="Parameter yang tidak didukung tidak membuat error">
          Bila model tidak mendukung sebuah parameter, misalnya model yang mengatur temperature sendiri, parameter itu dibuang dan
          namanya dicantumkan di header respons <code>x-aiagg-ignored-params</code>. Pengecualian: <code>n</code> selain 1 dan{' '}
          <code>logprobs</code> dibalas 400.
        </Callout>
      </DocSection>

      <DocSection id="respons" title="Respons">
        <CodeBlock
          code={examples[view]}
          tabs={
            <Tabs<ResponseView>
              tight
              value={view}
              onChange={setView}
              tabs={[
                { id: 'json', label: 'Biasa' },
                { id: 'sse', label: 'Streaming' },
              ]}
            />
          }
        />
        <p>
          <code>billing.amount</code> adalah biaya final request ini dalam Rupiah. <code>pricing_version_id</code> menunjuk tabel
          harga yang dipakai saat itu, jadi perubahan harga kemudian tidak mengubah tagihan lama.
        </p>
        <More label="Header respons" count={3}>
          <DocTable
            head={['Header', 'Isi']}
            rows={[
              {
                key: 'id',
                cells: [
                  <code>X-Request-Id</code>,
                  <>
                    ID request, sama dengan <code>id</code> di body. Ada juga di setiap error.
                  </>,
                ],
              },
              {
                key: 'ignored',
                cells: [<code>x-aiagg-ignored-params</code>, 'Parameter yang dibuang karena model tidak mendukungnya.'],
              },
              { key: 'retry', cells: [<code>Retry-After</code>, 'Hanya pada 429: berapa detik sebelum mencoba lagi.'] },
            ]}
          />
        </More>
      </DocSection>

      <DocSection id="streaming" title="Streaming">
        <p>
          Kirim <code>stream: true</code> untuk menerima jawaban sepotong demi sepotong lewat Server-Sent Events. Tambahkan{' '}
          <code>stream_options.include_usage</code> bila Anda ingin chunk terakhir berisi <code>usage</code> dan{' '}
          <code>billing</code>.
        </p>
        <LangCode code={streamSnippet(lang, { model })} lang={lang} langs={LANGS} onLang={setLang} />
        <ul>
          <li>
            Setiap frame diawali <code>data:</code>. Aliran ditutup dengan <code>data: [DONE]</code>.
          </li>
          <li>
            Baris <code>: keep-alive</code> dikirim saat koneksi diam. Abaikan saja; SDK OpenAI sudah melakukannya.
          </li>
          <li>Error sebelum chunk pertama (saldo, anggaran, rate limit, model tidak dikenal) dibalas sebagai status HTTP biasa.</li>
          <li>
            Error setelah aliran berjalan dikirim sebagai <code>event: error</code>, lalu <code>[DONE]</code>.
          </li>
        </ul>
        <Callout title="Koneksi terputus tetap ditagih" tone="warn">
          Memutus koneksi di tengah jalan menghentikan jawaban, tetapi token yang sudah dihasilkan provider tetap ditagih.
        </Callout>
      </DocSection>
    </>
  );
}

/* Tool calling dan JSON */

export function ToolsPage() {
  const { lang, setLang, model } = useDocs();

  return (
    <>
      <PageHead slug="tools">
        Biarkan model memanggil fungsi di aplikasi Anda, atau minta jawaban berupa JSON yang siap dibaca kode.
      </PageHead>

      <DocSection id="tool-calling" title="Tool calling">
        <p>Model bisa meminta aplikasi Anda menjalankan fungsi, lalu menjawab dengan hasilnya. Formatnya sama dengan OpenAI.</p>
        <ol>
          <li>
            Kirim daftar fungsi di <code>tools</code>. Setiap fungsi punya JSON Schema untuk argumennya.
          </li>
          <li>
            Bila model ingin memanggil fungsi, <code>finish_reason</code> bernilai <code>tool_calls</code>. Nama fungsi dan
            argumennya ada di <code>message.tool_calls</code>.
          </li>
          <li>
            Jalankan fungsinya. Kirim hasilnya sebagai pesan <code>role: &quot;tool&quot;</code> dengan <code>tool_call_id</code>{' '}
            yang sama, lalu model menjawab.
          </li>
        </ol>
        <LangCode code={toolsSnippet(lang, { model })} lang={lang} langs={LANGS} onLang={setLang} />
        <Callout>
          Model tanpa dukungan tools menolak request yang membawa <code>tools</code>. <code>tool_choice</code> yang memaksa (
          <code>required</code> atau nama fungsi) dibalas 400 <code>unsupported_parameter</code> pada model yang tidak bisa
          dipaksa. Pakai <code>auto</code> bila kode Anda harus jalan di banyak model.
        </Callout>
      </DocSection>

      <DocSection id="json" title="Output JSON">
        <p>
          Isi <code>response_format</code> supaya jawaban berupa JSON.
        </p>
        <DocTable
          head={[<code>type</code>, 'Hasil']}
          rows={[
            {
              key: 'obj',
              cells: [<code>json_object</code>, 'JSON yang valid dengan struktur bebas. Jelaskan bentuk yang Anda mau di prompt.'],
            },
            {
              key: 'schema',
              cells: [
                <code>json_schema</code>,
                <>
                  Mengikuti skema yang Anda kirim. Hanya untuk model bertanda <span className="tag">json</span> di{' '}
                  <DocLink slug="model" hash="katalog">
                    daftar model
                  </DocLink>
                  . Model lain membalas 400 <code>unsupported_parameter</code>.
                </>,
              ],
            },
          ]}
        />
        <LangCode code={jsonSnippet(lang, { model })} lang={lang} langs={LANGS} onLang={setLang} />
        <Callout>
          Bila <code>finish_reason</code> bernilai <code>length</code>, JSON terpotong karena <code>max_tokens</code> habis.
          Periksa ini sebelum membaca JSON, atau beri <code>max_tokens</code> yang lebih longgar.
        </Callout>
      </DocSection>
    </>
  );
}
